// useStatic.ts — polling hooks for /api/static/* endpoints.
// These are static source tables (AWQ shifts, retention survey, attrition, member snapshot).
// Data changes infrequently; polling interval defaults to 300s (5 min).

import { usePolling } from "./useDashboard";

// ── Row interfaces ────────────────────────────────────────────────────────────

export interface AttritionMonthlyRow {
  id?: number;
  year?: number | null;
  month?: string | null;
  start_count?: number | null;
  additions?: number | null;
  separations?: number | null;
  end_count?: number | null;
  attrition_pct?: number | null;
  notes?: string | null;
  [key: string]: unknown;
}

export interface RetentionSurveyRow {
  id?: number;
  response_id?: string | null;
  tenure_group?: string | null;
  actively_running?: string | null;
  goals?: string | null;
  challenges?: string | null;
  discontinue_factors?: string | null;
  improvement_suggestions?: string | null;
  [key: string]: unknown;
}

export interface RetentionThemeRow {
  id?: number;
  theme_table?: string | null;
  theme_label?: string | null;
  theme_value?: string | null;
  [key: string]: unknown;
}

export interface AwqShiftRow {
  id?: number;
  unit?: string | null;
  badge?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  position?: string | null;
  start_date?: string | null;
  end_date?: string | null;
  hours?: number | null;
  full_name_badge?: string | null;
  fiscal_year?: string | null;
  [key: string]: unknown;
}

export interface AwqVolunteerSummary {
  full_name_badge: string | null;
  badge: string | null;
  total_hours: number;
  shift_count: number;
}

export interface AwqStationHours {
  unit: string | null;
  total_hours: number;
  shift_count: number;
}

export interface AwqPositionBreakdown {
  position: string | null;
  shift_count: number;
  total_hours: number;
}

export interface AwqMonthlyTrend {
  month: string | null;
  shift_count: number;
  total_hours: number;
}

export interface AwqTotals {
  total_hours: number;
  total_shifts: number;
  unique_volunteers: number;
}

export interface AwqSummaryResponse {
  top_volunteers: AwqVolunteerSummary[];
  station_hours: AwqStationHours[];
  position_breakdown: AwqPositionBreakdown[];
  monthly_trend: AwqMonthlyTrend[];
  totals: AwqTotals;
}

export interface MemberSnapshotRow {
  id?: number;
  full_name_id?: string | null;
  joined_date?: string | null;
  years_service?: number | null;
  status?: string | null;
  snapshot_date?: string | null;
  [key: string]: unknown;
}

// ── Response shapes ───────────────────────────────────────────────────────────

export interface StaticListResponse<T> {
  data: T[];
  count: number;
}

// ── Hooks ─────────────────────────────────────────────────────────────────────

/** Fetches GET /api/static/attrition-monthly and polls every 5 min */
export function useAttritionMonthly(intervalMs = 300_000) {
  return usePolling<StaticListResponse<AttritionMonthlyRow>>(
    "/api/static/attrition-monthly",
    intervalMs,
  );
}

/** Fetches GET /api/static/retention-survey and polls every 5 min */
export function useRetentionSurvey(
  tenureBucket?: string,
  intervalMs = 300_000,
) {
  const url = tenureBucket
    ? `/api/static/retention-survey?tenure_group=${encodeURIComponent(tenureBucket)}`
    : "/api/static/retention-survey";
  return usePolling<StaticListResponse<RetentionSurveyRow>>(url, intervalMs);
}

/** Fetches GET /api/static/retention-themes and polls every 5 min */
export function useRetentionThemes(themeTable?: string, intervalMs = 300_000) {
  const url = themeTable
    ? `/api/static/retention-themes?theme_table=${encodeURIComponent(themeTable)}`
    : "/api/static/retention-themes";
  return usePolling<StaticListResponse<RetentionThemeRow>>(url, intervalMs);
}

/** Fetches GET /api/static/awq-summary and polls every 5 min */
export function useAwqSummary(intervalMs = 300_000) {
  return usePolling<AwqSummaryResponse>("/api/static/awq-summary", intervalMs);
}

/** Fetches GET /api/static/member-snapshot and polls every 5 min */
export function useMemberSnapshot(intervalMs = 300_000) {
  return usePolling<StaticListResponse<MemberSnapshotRow>>(
    "/api/static/member-snapshot",
    intervalMs,
  );
}
